import { GripVertical, X } from 'lucide-react';

import { Spinner } from '@/components/ui/Spinner';
import {
  AreaChart,
  BarList,
  Donut,
  formatCompact,
  formatNumber,
  type BarItem,
  type DonutSegment,
} from '@/features/dashboard/widgets';
import { useCompareData, useWidgetData } from '@/features/reporting/hooks';
import type { AggregateRow, WidgetConfig } from '@/features/reporting/types';
import type { ApiError } from '@/lib/errors';

const PALETTE = ['brand', 'sky', 'violet', 'amber', 'crimson'] as const;

export function ReportWidget({
  widget,
  onRemove,
}: {
  widget: WidgetConfig;
  onRemove: (id: string) => void;
}): JSX.Element {
  const isCompare = widget.mode === 'compare';
  const subtitle = isCompare
    ? `${(widget.datasetIds ?? []).length} datasets · ${widget.chartType}`
    : `${widget.datasetName} · ${widget.chartType}`;

  return (
    <section className="flex flex-col rounded-xl border border-ink-200/70 bg-white p-5 shadow-soft dark:border-white/10 dark:bg-ink-900">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-[15px] font-semibold tracking-tight text-ink-900 dark:text-ink-50">
            {widget.title}
          </h3>
          <p className="mt-0.5 truncate text-xs text-ink-500 dark:text-ink-400">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1 text-ink-400 dark:text-ink-500">
          <GripVertical className="h-4 w-4 cursor-grab opacity-50" />
          <button
            type="button"
            onClick={() => onRemove(widget.id)}
            className="rounded-md p-1 transition hover:bg-ink-100 hover:text-accent-crimson dark:hover:bg-white/10"
            title="Remove"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex min-h-[180px] flex-1 items-center justify-center">
        {isCompare ? <CompareBody widget={widget} /> : <SingleBody widget={widget} />}
      </div>
    </section>
  );
}

function SingleBody({ widget }: { widget: WidgetConfig }): JSX.Element {
  const query = useWidgetData(widget);
  return (
    <WidgetBody
      widget={widget}
      rows={query.data ?? []}
      isPending={query.isPending}
      isError={query.isError}
      error={query.error as ApiError | null}
    />
  );
}

function CompareBody({ widget }: { widget: WidgetConfig }): JSX.Element {
  const q = useCompareData(widget);
  return (
    <WidgetBody widget={widget} rows={q.rows} isPending={q.isPending} isError={q.isError} error={q.error} />
  );
}

function WidgetBody({
  widget,
  rows,
  isPending,
  isError,
  error,
}: {
  widget: WidgetConfig;
  rows: AggregateRow[];
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
}): JSX.Element {
  if (isPending) return <Spinner />;
  if (isError) return <p className="px-2 text-center text-xs text-accent-crimson">{error?.message ?? 'Failed to load.'}</p>;
  if (rows.length === 0) return <p className="text-sm text-ink-400 dark:text-ink-500">No data.</p>;
  return <Chart widget={widget} rows={rows} />;
}

function Chart({ widget, rows }: { widget: WidgetConfig; rows: { label: string; value: number }[] }): JSX.Element {
  switch (widget.chartType) {
    case 'kpi': {
      const value = rows.length === 1 ? rows[0]!.value : rows.reduce((s, r) => s + r.value, 0);
      return (
        <div className="text-center">
          <div className="font-display text-4xl font-bold text-ink-900 dark:text-ink-50">{formatNumber(value)}</div>
          <div className="mt-1 text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">{widget.title}</div>
        </div>
      );
    }
    case 'pie': {
      const total = rows.reduce((s, r) => s + r.value, 0);
      const segments: DonutSegment[] = rows.slice(0, 6).map((r, i) => ({
        label: r.label || '(blank)',
        value: Math.round(r.value),
        accent: PALETTE[i % PALETTE.length]!,
      }));
      return <Donut segments={segments} centerValue={formatCompact(total)} centerLabel="total" />;
    }
    case 'line': {
      const points = rows.map((r) => ({ ts: r.label, value: r.value }));
      return (
        <div className="w-full">
          <AreaChart points={points} accent="sky" />
        </div>
      );
    }
    case 'table':
      return (
        <div className="w-full overflow-y-auto" style={{ maxHeight: 260 }}>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-ink-100 dark:divide-white/5">
              {rows.map((r) => (
                <tr key={r.label}>
                  <td className="py-1.5 pr-3 text-ink-700 dark:text-ink-200">{r.label || '(blank)'}</td>
                  <td className="py-1.5 text-right font-mono text-ink-800 dark:text-ink-100">
                    {formatNumber(r.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'bar':
    default: {
      const items: BarItem[] = rows.map((r, i) => ({
        label: r.label || '(blank)',
        value: r.value,
        display: formatCompact(r.value),
        accent: PALETTE[i % PALETTE.length]!,
      }));
      return (
        <div className="w-full">
          <BarList items={items} />
        </div>
      );
    }
  }
}
